import Vue from 'vue';
import VueRouter from 'vue-router';
import jwtDecode from 'jwt-decode';
import globalStorageMixin from '@/mixins/global-storage.js';
import { waitHydrated } from '@/mixins/ssr.js';

// Ignore navigation errors of these types:
// https://v3.router.vuejs.org/guide/advanced/navigation-failures.html#navigationfailuretype
const SUPPRESS = [ 'duplicated', 'redirected', 'aborted' ];

function onNavigationFailure(e) {
  if (!SUPPRESS.some(x => VueRouter.isNavigationFailure(e, VueRouter.NavigationFailureType[x])))
    console.error('Navigation failed', e);
  return e;
}

const _push = VueRouter.prototype.push;
VueRouter.prototype.push = function push(routerLocation) {
  return _push.call(this, routerLocation).catch(onNavigationFailure);
}

const _replace = VueRouter.prototype.replace;
VueRouter.prototype.replace = function replace(routerLocation) {
  return _replace.call(this, routerLocation).catch(onNavigationFailure);
}

Vue.use(VueRouter);

// https://www.digitalocean.com/community/tutorials/vuejs-lazy-loading-vue-cli-3-webpack
function loadView(view) {
  return () => import(/* webpackChunkName: "view-[request]" */ `@/views/${view}.vue`);
}

function loadComponent(component) {
  return () => import(/* webpackChunkName: "view-[request]" */ `@/components/${component}.vue`);
}

// A regex that you can use with route params to match UUIDs only
const UUID = '([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})';

// In every case except these, the category header can be directly created from the legacy 'start' category by
// appending the suffix "-cards"
const START_CATEGORY_REDIRECTS = { baby: 'new-baby', condolences: 'sympathy', congrats: 'congratulations' };

const routes = [
  { path: '/', name: 'default',
    redirect(to) {
      return { name: globalStorageMixin.data().global.jwt ? 'catalog' : 'start' };
    }
  },
  { path: '/start', name: 'start', component: loadView('Start') },

  { path: '/start/group-cards', name: 'start-group-cards', component: loadView('StartGroupCards') },
  { path: '/start/invitations', name: 'start-invitations', component: loadView('StartInvitations') },
  { path: '/start/gift-cards', name: 'start-gift-cards', component: loadView('StartGiftCards') },
  { path: '/start/holidays', name: 'start-holidays', component: loadView('StartHolidays') },
  { path: '/start/video-maker', name: 'start-video-maker', component: loadView('StartVideoMaker') },
  { path: '/start/recognition', name: 'start-recognition', component: loadView('StartRecognition') },
  { path: '/start/automation', name: 'start-automation', component: loadView('StartAutomation') },
  { path: '/start/bulk-email', name: 'start-bulk-email', component: loadView('StartBulkEmail') },
  { path: '/start/embedding-api', name: 'start-embedding-api', component: loadView('StartEmbeddingApi') },
  { path: '/start/business', name: 'start-business', component: loadView('StartBusiness') },

  // Defunct / not maintained
  { path: '/start/yearbooks', name: 'start-yearbooks', component: loadView('StartYearbooks') },
  { path: '/physical-virtual-cards', name: 'physical-virtual-cards', component: loadView('PhysicalVirtualCards') },

  // { path: `/videos/:templateId${UUID}?`, name: 'catalog-video', props: true, component: loadView('CatalogVideo') },
  { path: `/videos`, name: 'catalog-video', component: loadView('CatalogVideo'),
    children: [
      { path: `:templateId${UUID}`, name: 'video-template', props: true, component: loadView('VideoThemeDialog') }
    ]
  },

  { path: `/cards/:selectedId${UUID}?`, name: 'catalog', props: true, component: loadView('Catalog') },
  { path: '/categories', name: 'catalog-categories', component: loadView('CatalogCategories') },
  { path: `/categories/:category/:selectedId${UUID}?`, name: 'catalog-category', props: true, component: loadView('CatalogCategory') },
  { path: '/gift-cards/:providerId?', name: 'catalog-gift-cards', props: true, component: loadView('CatalogGiftCards') },
  { path: `/project/:orderId`, name: 'project', props: true, component: loadView('Project') },
  { path: '/account', name: 'account', component: loadView('Account'),
    children: [
      { path: 'overview', name: 'account-overview', component: loadView('AccountOverview') },
      { path: 'orders/:orderId?', name: 'account-orders', props: true, component: loadView('AccountOrders') },
      { path: 'signed', name: 'account-signed', component: loadView('AccountSigned') },
      { path: 'received', name: 'account-received', component: loadView('AccountReceived') },
      { path: 'contacts', name: 'account-contacts', component: loadView('AccountContacts') },
      { path: 'automation', name: 'account-automations', component: loadView('AccountAutomations') },
      { path: `automation/:id${UUID}`, name: 'account-automation', props: true, component: loadView('AccountAutomation') },
      { path: 'transactions', name: 'account-transactions', component: loadView('AccountTransactions') },
      { path: 'delete', name: 'account-delete', component: loadView('AccountDelete') },
      { path: 'design', name: 'account-design', component: loadView('AccountDesign'),
        redirect: { name: 'account-design-cards' },
        children: [
          { path: 'overview', name: 'account-design-overview', component: loadView('AccountDesignOverview') },
          { path: 'cards/:designId?', name: 'account-design-cards', props: true, component: loadView('AccountDesignCards') },
          { path: 'videos/:templateId?', name: 'account-design-videos', props: true, component: loadView('AccountDesignVideos') }
        ]
      },
      { path: 'org', name: 'account-org', component: loadView('AccountOrg'),
        redirect: { name: 'account-org-overview' },
        children: [
          { path: 'overview', name: 'account-org-overview', component: loadView('AccountOrgOverview') },
          { path: 'users', name: 'account-org-users', component: loadView('AccountOrgUsers') },
          { path: 'user-activity', name: 'account-org-user-activity', component: loadView('AccountOrgUserActivity') },
          { path: 'orders', name: 'account-org-orders', component: loadView('AccountOrgOrders') },
          { path: 'automations', name: 'account-org-automations', component: loadView('AccountOrgAutomations') },
          { path: 'unsubscribes', name: 'account-org-unsubscribes', component: loadView('AccountOrgUnsubscribes') },
          { path: 'branding', name: 'account-org-branding', component: loadView('AccountOrgBranding') },
          { path: 'embedding', name: 'account-org-embedding', component: loadView('AccountOrgEmbedding') },
          { path: 'keys', name: 'account-org-keys', component: loadView('AccountOrgKeys') }
        ]
      }
    ]
  },
  { path: '/design', name: 'design', component: loadView('Design') },
  { path: '/video-template-docs', name: 'video-template-docs', component: loadView('VideoTemplateDocs') },
  { path: '/api-docs', name: 'api-docs', component: loadView('APIDocs') },
  { path: '/featured/index', name: 'featured-index', component: loadView('FeaturedIndex') },
  { path: `/featured/:id${UUID}?`, name: 'featured', props: true, component: loadView('Featured') },
  { path: '/admin', name: 'admin', component: loadView('Admin'),
    children: [
      { path: 'review', name: 'admin-review', component: loadView('AdminReview') },
      { path: 'cards/:designId', name: 'admin-review-card', props: true, component: loadView('AdminReviewCard') },
      { path: 'videos/:templateId', name: 'admin-review-video', props: true, component: loadView('AdminReviewVideo') },
      { path: 'user', name: 'admin-user', component: loadView('AdminUser'),
        children: [
          { path: ':userId', name: 'admin-user-details', props: true, component: loadView('AdminUserDetails') }
        ]
      },
      { path: 'users', name: 'admin-users', component: loadView('AdminUsers') },
      { path: 'order/:token?', name: 'admin-order', component: loadView('AdminOrder') },
      { path: 'tracking', name: 'admin-tracking', props: true, component: loadView('AdminTracking') },
      { path: 'domain-risks', name: 'admin-domain-risks', component: loadView('AdminDomainRisks') },
      { path: 'orgs/:orgId?', name: 'admin-orgs', props: true, component: loadView('AdminOrgs') },
      { path: 'promo-codes', name: 'admin-promo-codes', component: loadView('AdminPromoCodes') },
      { path: 'payments', name: 'admin-payments', component: loadView('AdminPayments') },
      { path: 'manage-catalog', name: 'admin-manage-catalog', component: loadView('AdminCatalog') },
      { path: 'manage-gift-card-catalog', name: 'admin-manage-gift-card-catalog', component: loadView('AdminGiftCardCatalog') },
      { path: 'gift-cards', name: 'admin-gift-cards', component: loadView('AdminGiftCards') },
      { path: 'gift-card-claims', name: 'admin-gift-card-claims', component: loadView('AdminGiftCardClaims') },
      { path: 'categories/:name?', name: 'admin-categories', props: true, component: loadView('AdminCatalogHeaders') },
      { path: 'designers', name: 'admin-designers', component: loadView('AdminDesigners') },
      { path: 'designers/:userId', name: 'admin-designer-designs', props: true, component: loadView('AdminDesignerDesigns') },
      { path: 'unsubscribes', name: 'admin-unsubscribes', component: loadView('AdminUnsubscribes') },
      {
        path: 'faq-editor', name: 'admin-faq-editor', component: loadView('AdminFAQEditor'),
        children: [
          { path: ':ids+', name: 'admin-faq-editor-article', props: true, component: loadView('AdminFAQEditorArticle') }
        ]
      },

      { path: 'blog-editor/:id?', name: 'admin-blog-editor', props: true, component: loadView('AdminBlogEditor') },
      { path: 'pricing', name: 'admin-pricing', component: loadView('AdminPricing') },
      { path: 'customer-segments', name: 'admin-customer-segments', component: loadView('AdminCustomerSegments') },
      { path: 'analytics', name: 'admin-analytics', component: loadView('AdminAnalytics') },
      { path: 'web-media', name: 'admin-web-media', component: loadView('AdminWebMedia') }
    ]
  },
  { path: `/sign/:orderId${UUID}`, name: 'card-sign', props: true, component: loadView('CardSign') },
  { path: `/video-project/:projectId${UUID}`, name: 'video-project', props: true, component: loadView('GroupVideoProject') },
  { path: `/open/:readToken${UUID}`, name: 'card-open', props: true, component: loadView('CardOpen') },
  { path: '/img-to-design', name: 'img-to-design', component: loadView('ImgToDesign') },

  { path: '/bulk-order', name: 'batch-order', component: loadView('BatchOrder') },

  {
    path: '/automation',
    name: 'automation',
    component: loadView('Automation'),
    children: [
      { path: 'new', name: 'automation-new', component: loadView('AccountAutomation') }
    ]
  },

  { path: '/bulk-email', name: 'bulk-email', component: loadView('BulkEmail') },
  { path: `/bulk-email/:campaignId${UUID}`, name: 'bulk-email-campaign', props: true, component: loadView('BulkEmailCampaign') },
  { path: '/file-transfer', name: 'file-transfer', component: loadView('FileTransfer') },
  { path: '/unsubscribe/confirm', name: 'unsubscribe-confirm', component: loadView('UnsubscribeConfirm') },
  { path: '/unsubscribe/status', name: 'unsubscribe-status', component: loadView('UnsubscribeStatus') },
  { path: '/report-sender', name: 'report-sender', component: loadView('ReportSender') },
  { path: '/about', name: 'about', component: loadView('About') },
  { path: '/faq', name: 'faq', component: loadView('FAQ') },
  { path: '/faq/topics', name: 'faq-topics', component: loadView('FAQTopics') },
  { path: '/faq/:ids+', name: 'faq-article', props: true, component: loadView('FAQArticle') },
  { path: '/blog', name: 'blog', component: loadView('Blog') },
  { path: '/blog/:urlTitle', name: 'blog-post', props: true, component: loadView('BlogPost') },
  { path: '/pricing', name: 'pricing', component: loadView('Pricing') },
  { path: '/international-gift-cards', name: 'international-gift-cards', component: loadView('InternationalGiftCards') },
  { path: '/privacy-policy', name: 'privacy-policy', component: loadView('PrivacyPolicy') },
  { path: '/cookies', name: 'cookies', component: loadView('Cookies') },
  { path: '/terms-and-conditions', name: 'terms-and-conditions', component: loadView('TermsAndConditions') },
  { path: '/integrations', name: 'integrations', component: loadView('Integrations') },
  { path: '/site-map', name: 'site-map', component: loadView('SiteMap') },
  { path: '/release-notes/:date?', name: 'release-notes', props: true, component: loadView('ReleaseNotes') },
  { path: '/social', name: 'social-media', component: loadView('SocialMedia') },
  { path: '/sign-in/:payload', name: 'sign-in', props: true, component: loadView('SignIn') },
  { path: '/notify', name: 'notify', component: loadView('Notify') },

  { path: '/jobs/operations-and-growth-manager', name: 'job-posting', component: loadView('JobPosting') },

  // We prefix "private" routes with an underscore - these are omitted from indexing in robots.txt
  { path: '/_', name: '_blank', component: loadView('_Blank') },
  { path: '/_/integration/:id', name: '_integration', props: true, component: loadView('_Integration') },
  { path: '/_/capture', name: '_capture', component: loadView('_Capture') },
  { path: '/_/capture-env-background', name: '_capture-env-background', component: loadView('_CaptureEnvBackground') },
  { path: '/_/print/:token', name: '_print', props: true, component: loadView('_Print') },
  { path: '/_/embed', name: '_embed-root', component: loadView('_EmbedRoot'),
    children: [
      { path: 'checkout', name: 'embed-checkout', component: loadView('EmbedCheckout') },
      { path: 'catalog', name: 'embed-catalog', component: loadView('EmbedCatalog') },
      { path: 'sign/:orderId', name: 'embed-sign', props: true, component: loadView('CardSign') },
      { path: 'open/:readToken', name: 'embed-open', props: true, component: loadView('CardOpen')
      }
    ]
  },
  { path: '/_/reset', name: '_reset',
    redirect() {
      try {
        localStorage.clear();
      } catch (e) {}
      const data = globalStorageMixin.data().global;
      data.jwt = null;
      data.order = null;
      data.localBlobSrcMap = {};
      data.cookies = null;
      return { name: 'default' };
    }
  },
  { path: '/_/embedded-order', name: '_embedded-order',
    redirect() {
      let order;
      try {
        order = JSON.parse(localStorage.getItem('ellacard.embedded_order'));
        delete order.reply_to;
        localStorage.removeItem('ellacard.embedded_order');
      } catch (e) {}
      return { name: 'project', params: { orderId: 'new', order }, hash: '#type' };
    }
  },

  // Legacy redirects
  { path: '/business', name: 'business', redirect: { name: 'start-business' } },
  { path: '/start/business/internal', redirect: { name: 'start-recognition' } },
  { path: '/start/business-internal', redirect: { name: 'start-recognition' } },
  { path: '/start/business/outreach', redirect: { name: 'start-business' } },
  { path: '/start/business-outreach', redirect: { name: 'start-business' } },
  { path: '/start/charity', redirect: { name: 'start-business' } },
  { path: '/start/group-videos', redirect: { name: 'start-video-maker' } },
  { path: '/start/holiday', redirect: { name: 'start-holidays' } },
  { path: '/batch-order', redirect: { name: 'batch-order' } },
  { path: '/available-gift-cards', redirect: { name: 'international-gift-cards' } },
  { path: '/account/organization-tools', redirect: { name: 'account-org' } },
  { path: '/account/designs', redirect: { name: 'account-design-cards' } },
  { path: '/design/videos/docs', redirect: { name: 'video-template-docs' } },
  { path: '/design/cards/:designId?', redirect: { name: 'account-design-cards' } },
  { path: '/design/videos/:templateId?', redirect: { name: 'account-design-videos' } },
  { path: '/design/payments', redirect: { name: 'account-design-overview' } },
  { path: '/img-to-card', redirect: { name: 'img-to-design' } },
  { path: '/confirm-unsubscribe', redirect: { name: 'unsubscribe-confirm' } },
  { path: '/unsubscribe', redirect: { name: 'unsubscribe-status' } },
  { path: '/account/signed/:orderId',
    redirect(to) {
      return { name: 'account-orders', params: to.params, hash: to.hash };
    }
  },
  { path: '/start/:category',
    redirect(to) {
      const category = (START_CATEGORY_REDIRECTS[to.params.category] || to.params.category) + '-cards';
      return { name: 'catalog-category', params: { category } };
    }
  },

  { path: '*', name: 'not-found', component: loadView('RouteNotFound') }
];

function scrollBehavior(to, from, savedPosition) {
  // Hash scrolling is now generally handled by the 'router.afterEach' function below so that we can wait until pages
  // have become hydrated to make sure any target hash IDs exist
  if (savedPosition)
    // The 'savedPosition' is only provided for browser forward / back navigation events
    return savedPosition;
  if (to.matched[0]?.name == from.matched[0]?.name)
    // Don't scroll for nested views
    return;
  return { x: 0, y: 0 };
}

const router = new VueRouter({ mode: 'history', routes, scrollBehavior });

const STRIP_QUERY_PREFIXES = [ 'g_ad_' ];
const PERSIST_QUERY_PARAMS = [ 'gclid', 'msclkid' ];

router.beforeEach((to, from, next) => {
  window.SSR_LOADED = router.hydrated = false;
  if (to.query) {
    let found = false;
    const query = { ...to.query };
    for (const key of Object.keys(query))
      for (const prefix of STRIP_QUERY_PREFIXES)
        if (key.startsWith(prefix))
          found = delete query[key];
    if (found) {
      // Make sure we capture the original navigation event before we strip the parameters
      setTimeout(() => $event.log('nav', _.omitBy(JSON.parse(JSON.stringify(_.pick(to, [ 'name', 'params', 'hash', 'query' ]))), _.isEmpty)));
      return next({ name: to.name, params: to.params, hash: to.hash, query });
    }
  }

  if (from.query) {
    const add = {};
    for (const p of PERSIST_QUERY_PARAMS)
      if (from.query[p] && !(to.query || {})[p])
        // Make sure we don't drop persistent query params
        add[p] = from.query[p];
    if (Object.values(add).length)
      return next({ name: to.name, params: to.params, hash: to.hash, query: { ...to.query, ...add } });
  }

  next();
});

const prototype = Object.getPrototypeOf(Object.getPrototypeOf(router.history));
const _confirmTransition = prototype.confirmTransition;
prototype.confirmTransition = function(route, onComplete, onAbort) {
  return _confirmTransition.call(
    this,
    route,
    onComplete,
    (...args) => {
      window.SSR_LOADED = router.hydrated = true;
      window.dispatchEvent(new Event('ssr-loaded'));
      return onAbort(...args);
    }
  );
};

function getRouterViewMeta(r) {
  const meta = { title: 'Ellacard', $index: true };
  let subviews;
  try {
    subviews = r.matched.map(
      x => Object.assign({}, x.components.default.routerViewMeta || {}, x.instances.default.routerViewMeta || {})
    );
  } catch (e) {
    // Presumably a router view instance is unavailable and likely hasn't loaded yet
    return { title: 'Ellacard', $index: false };
  }
  // Generally, the child-most subview dictates the metadata. Specific properties like '$index', '$hide', and
  // '$notFound' apply if they're found at any level. The '$subtitle' property is used to dynamically construct a
  // 'title'.
  Object.assign(meta, subviews.last);
  for (const { $index, $hide, $notFound } of subviews) {
    if ($notFound)
      return { title: 'Not found', $index: false, $notFound: true };
    if ($index == false)
      meta.$index = false;
    if ($hide)
      meta.$hide = [ ...(meta.$hide || []), ...$hide ];
  }
  for (const { title, $subtitle } of subviews.reverse()) {
    if (title) {
      meta.title = title;
      break;
    }
    if ($subtitle)
      meta.title = $subtitle + ' | ' + meta.title;
  }
  meta.$canonical ||= r.path;
  if (!meta.$canonical.startsWith(location.origin))
    meta.$canonical = location.origin + meta.$canonical;
  meta.url ||= meta.$canonical;
  return meta;
}

function addToHead(tag, inner, attrs) {
  const node = document.createElement(tag);
  node.setAttribute('_router-view-meta', '');
  if (inner)
    node.textContent = inner;
  for (const [ key, value ] of Object.entries(attrs || {}))
    node.setAttribute(key, value);
  document.head.appendChild(node);
}

function truncate(text, n) {
  return (text.length > n) ? (text.slice(0, n - 1).split(' ').slice(0, -1).join(' ') + '…') : text;
}

function updateRouteMeta(route) {
  const meta = getRouterViewMeta(route);
  for (const el of document.querySelectorAll('[_router-view-meta]'))
    el.remove();

  document.title = meta.title;

  if (!meta.$index)
    addToHead('meta', '', { name: 'robots', content: 'noindex' });

  if (!meta.$notFound) {
    addToHead('link', '', { rel: 'canonical', href: meta.$canonical });

    if (meta.$product) {
      addToHead('meta', '', { property: 'product:price:amount', content: meta.$product.price });
      addToHead('meta', '', { property: 'product:price:currency', content: meta.$product.cur });
      addToHead('meta', '', { property: 'og:availability', content: 'instock' });
    }

    if (meta.$schema)
      addToHead(
        'script',
        JSON.stringify({ '@context': 'https://schema.org/', ...meta.$schema }),
        { 'type': 'application/ld+json' }
      );

    for (const [ k, v ] of Object.entries(meta))
      if (!k.startsWith('$') && v)
        addToHead(
          'meta',
          '',
          {
            property: `og:${k}`,
            content: truncate((v || '').replace(/"/g, '&quot;'), 255), ...((k == 'description') ? { name: 'description' } : {})
          }
        );
  }
}

router.afterEach(async (to, from) => {
  // Navigation has been completed, but the view components have not necessarily even been created by this point - DOM
  // updates aren't triggered until after the global 'afterEach' hook:
  // https://router.vuejs.org/guide/advanced/navigation-guards#The-Full-Navigation-Resolution-Flow
  await waitHydrated(to);
  updateRouteMeta(to);

  // Scroll to any dynamically-loaded hash sections if applicable
  if (to.hash && (to.hash != from.hash))
    document.getElementById(to.hash.substr(1))?.scrollIntoView({ behavior: 'smooth' });

  window.SSR_LOADED = router.hydrated = true;
  window.dispatchEvent(new Event('ssr-loaded'));

  // https://developers.google.com/analytics/devguides/collection/gtagjs/pages#page_view_event
  gtag('event', 'page_view', { page_title: document.title, page_location: location.href, page_path: to.fullPath });

  // Note that we use JSON parse / stringify to recursively remove all undefined properties from the route object
  $event.log('nav', _.omitBy(JSON.parse(JSON.stringify(_.pick(to, [ 'name', 'params', 'hash', 'query' ]))), _.isEmpty));

  router.loaded ||= true;
});

Vue.util.defineReactive(router, 'loaded', false);
Vue.util.defineReactive(router, 'hydrated', false);
Object.defineProperty(router, 'currentRouteMeta', { get: () => getRouterViewMeta(router.currentRoute) });

export default router;
